import { BlockAuthForm } from "@-/dev.improve.haniverse.routes.nav.app.auth._components.block-auth-form";
import { GetStartedAuth } from "@-/dev.improve.haniverse.components.ui.get-started-auth";
import { View } from "@-/com.npmjs.react-native";
import { useState } from "react";
import type { AuthSheetMode, AuthSheetSignUpKind } from "#auth-sheet-context";
import { AuthSheetModeToggle } from "#auth-sheet-mode-toggle";
import { useLeaveAuthSheet } from "#use-leave-auth-sheet";

/**
 * Height reserved for the revealed email fields in BOTH modes, so switching between Log In and Sign
 * Up never resizes the sheet. It fits the tallest form, Log In's bare `AuthForm`: email, password,
 * submit and the forgot-password link, each a 48pt row with 12pt gaps. The unrevealed email
 * affordance is an outline button on web and a de-emphasized link on mobile, so this reserve only
 * applies once it is pressed and the fields replace it.
 */
const EMAIL_FORM_MIN_HEIGHT = 4 * 48 + 3 * 12;

/**
 * The one auth sheet: a Log In / Sign Up toggle over the same provider rows (Apple, Google, and
 * email). Both modes render the same rows, so the sheet is the same height either way; only the
 * verb on the buttons and the fields the email control reveals differ.
 *
 * - Log In uses "Log in with …" and reveals email + password sign-in, with forgot/reset password,
 *   the verification code and the two-factor challenge switching in place (`BlockAuthForm`).
 * - Sign Up uses "Continue with …" and reveals the Get Started email field (a local-only guest
 *   address), or email + password sign-up when activating an address or when a caller asked for an
 *   account (`signUpKind`).
 *
 * The welcome page's landscape layout also renders it inline, as its right half.
 */
export function AuthSheetContent({
  close,
  email,
  initialMode,
  presentation = "sheet",
  signUpKind,
}: {
  close: () => void;
  email: string;
  initialMode: AuthSheetMode;
  /** Web's welcome card opens directly onto the complete account form. */
  presentation?: "card" | "sheet";
  signUpKind: AuthSheetSignUpKind;
}) {
  const leave = useLeaveAuthSheet(close);
  const [mode, setMode] = useState(initialMode);
  // Activation already has the address, so it opens straight onto the password field.
  const [isEmailRevealed, setIsEmailRevealed] = useState(
    initialMode === "signUp" && signUpKind === "account" && email.length > 0,
  );
  const isGetStarted = mode === "signUp" && signUpKind === "getStarted";
  // Signing up (or "Get Started") makes a new learner, whose app opens on the onboarding.
  const leaveForMode = () => leave({ isNewAccount: mode === "signUp" });

  if (presentation === "card") {
    return (
      <View testID="auth-sheet">
        <BlockAuthForm
          card
          initialEmail={email}
          initialMode={initialMode}
          onModeChange={(nextMode) => {
            if (nextMode === "signIn" || nextMode === "signUp") {
              setMode(nextMode);
            }
          }}
          onSignedIn={leaveForMode}
        />
      </View>
    );
  }

  return (
    <View className="gap-5 pb-2" testID="auth-sheet">
      <AuthSheetModeToggle mode={mode} onModeChange={setMode} />
      <GetStartedAuth
        emailFormMinHeight={EMAIL_FORM_MIN_HEIGHT}
        isEmailRevealed={isEmailRevealed}
        mode={mode}
        onContinue={leaveForMode}
        onRevealEmail={() => setIsEmailRevealed(true)}
        renderEmailForm={
          isGetStarted
            ? undefined
            : () => (
                <BlockAuthForm
                  bare
                  initialEmail={email}
                  initialMode={mode}
                  // Each mode starts from its own fresh form state.
                  key={mode}
                  onSignedIn={leaveForMode}
                />
              )
        }
      />
    </View>
  );
}
